import { FieldProps, getIn } from 'formik'
import React, { useCallback } from 'react'
import { SingleValue } from 'react-select'

import { Error, FieldContainer, Label } from '../../../../layout'
import { SelectField as Select } from '../../style'

type Props = OwnProps & FieldProps

interface OwnProps {
  options: Option[]
  label?: string
  required?: boolean
  clearable?: boolean
  onChange: (arg: string | number | undefined | null) => void
  searchable?: boolean
  placeholder?: string
  disabled: boolean
  unsetAfterSelect?: boolean
  hideArrow: boolean
  dataTestId?: string
}

interface Option {
  label: string
  value: string
  isDisabled?: boolean
}

export const SelectField: React.FunctionComponent<Props> = ({
  clearable,
  field,
  form,
  onChange,
  options,
  label,
  placeholder,
  required,
  searchable,
  disabled,
  unsetAfterSelect = false,
  hideArrow = false,
  dataTestId
}) => {
  const selectedOption = options.find(o => o.value === field.value) ?? null

  const handleChange = useCallback(
    (option: SingleValue<Option>) => {
      if (unsetAfterSelect) {
        onChange(option ? option.value : null)
        form.setFieldValue(field.name, null)
        form.setFieldTouched(field.name, true)
        return
      }
      const value = option ? option.value : null
      if (onChange && value !== field.value) {
        onChange(value)
      }
      form.setFieldValue(field.name, value)
      form.setFieldTouched(field.name, true)
    },
    [field, form, onChange, unsetAfterSelect]
  )

  const handleNoOptionsMessage = useCallback(() => '- Ei tuloksia -', [])

  const fieldError = getIn(form.errors, field.name)
  const fieldTouched = getIn(form.touched, field.name)
  const hasErrors = Boolean(fieldError) && Boolean(fieldTouched)

  return (
    <FieldContainer data-testid={dataTestId}>
      {label && (
        <Label htmlFor={field.name} required={required}>
          {label}
        </Label>
      )}

      <Select
        classNamePrefix={'Select'}
        isDisabled={disabled}
        value={selectedOption}
        onChange={handleChange}
        options={options}
        placeholder={placeholder ? placeholder : 'Valitse...'}
        noOptionsMessage={handleNoOptionsMessage}
        isClearable={clearable}
        isSearchable={searchable}
        menuPlacement="auto"
        hideArrow={hideArrow}
      />
      {hasErrors && field.name !== '' && <Error className="error-tooltip">{fieldError}</Error>}
    </FieldContainer>
  )
}
